import React from "react";
import { Box, Chip, styled, useMediaQuery, useTheme } from "@mui/material";
import isPropValid from "@emotion/is-prop-valid";
import { FilterItem, FilterValue } from "../types";

const ChipsContainer = styled(Box, {
  shouldForwardProp: (prop: string) =>
    isPropValid(prop) || (prop !== "shouldBeScrollable" && prop !== "size"),
})<{ shouldBeScrollable: boolean; size: "small" | "medium" | "large" }>`
  display: flex;
  flex-wrap: ${({ shouldBeScrollable }) =>
    shouldBeScrollable ? "nowrap" : "wrap"};
  gap: ${({ theme }) => theme.spacing(2.5)};
  margin-right: ${({ shouldBeScrollable, theme }) =>
    shouldBeScrollable ? theme.spacing(-6) : 0};
  padding-right: ${({ shouldBeScrollable, theme }) =>
    shouldBeScrollable ? theme.spacing(6) : 0};
  overflow: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  .MuiChip-root {
    height: ${({ size }) =>
      size === "large" ? "36px" : size === "medium" ? "30px" : "24px"};
  }
`;

interface Props {
  options: FilterItem[];
  selectedOptions: FilterValue[];
  setSelectedOptions: (options: FilterValue[]) => void;
  scrollableContainer?: boolean;
  hasAllOption?: boolean;
  singleSelectMode?: boolean;
  infoMode?: boolean;
  size?: "small" | "medium" | "large";
}

export const Chips = ({
  options,
  selectedOptions,
  setSelectedOptions,
  scrollableContainer = false,
  hasAllOption = true,
  singleSelectMode = false,
  infoMode = false,
  size = "large",
}: Props) => {
  const theme = useTheme();
  const isMD = useMediaQuery(theme.breakpoints.down("md"));

  const onChipSelect = (value: FilterValue) => {
    if (infoMode) return;

    if (singleSelectMode) {
      if (!selectedOptions.includes(value)) setSelectedOptions([value]);
      return;
    }

    if (value === "all") {
      return setSelectedOptions(["all"]);
    }

    if (selectedOptions.includes(value)) {
      const temp = selectedOptions.filter((chip) => chip !== value);
      return setSelectedOptions(
        temp.length === 0 ? (hasAllOption ? ["all"] : []) : temp
      );
    }
    setSelectedOptions([...selectedOptions.filter((x) => x !== "all"), value]);
  };

  const shouldBeScrollable = scrollableContainer && isMD;

  return (
    <ChipsContainer shouldBeScrollable={shouldBeScrollable} size={size}>
      {options.map((type) => (
        <Chip
          variant="outlined"
          key={`key-${type.value}`}
          data-testid={`depots-chip-${type.value}`}
          label={type.text}
          icon={type.icon}
          color={
            !type.clickable || selectedOptions.includes(type.value)
              ? "primary"
              : "secondary"
          }
          onClick={type.clickable ? () => onChipSelect(type.value) : () => {}}
          sx={{
            height: "36px",
            cursor: infoMode ? "default" : "pointer",
          }}
        />
      ))}
    </ChipsContainer>
  );
};
